import React from "react";
import { StyledTHead, StyledTh, SortIcon } from "../styles";
import { LABELS } from "../labels";
import type { SortState } from "../types";

interface Props {
	langs: string[];
	sort: SortState;
	onSort: (col: SortState["column"]) => void;
	allSelected: boolean;
	onSelectAll: () => void;
	onSelectNone: () => void;
}

type ColDef = {
	key: SortState["column"] | "_checkbox" | "_actions" | "_tags";
	label: string;
	sortable: boolean;
	width?: string;
};

const COLUMNS: ColDef[] = [
	{ key: "_checkbox", label: "", sortable: false, width: "36px" },
	{ key: "key", label: LABELS.colKey, sortable: true, width: "220px" },
	{ key: "screen", label: LABELS.colScreen, sortable: true, width: "110px" },
	{ key: "group", label: LABELS.colGroup, sortable: true, width: "100px" },
	{ key: "currentText", label: LABELS.colText, sortable: true },
	{ key: "_tags", label: LABELS.colTags, sortable: false, width: "180px" },
	{ key: "status", label: LABELS.colStatus, sortable: true, width: "90px" },
	{ key: "updatedAt", label: LABELS.colUpdated, sortable: true, width: "100px" },
	{ key: "_actions", label: "", sortable: false, width: "40px" },
];

function buildColumns(langs: string[]): ColDef[] {
	const out: ColDef[] = [];
	COLUMNS.forEach((col) => {
		out.push(col);
		if (col.key === "currentText") {
			langs.forEach((lang) =>
				out.push({ key: `translation:${lang}` as any, label: LABELS.langName(lang), sortable: true })
			);
		}
	});
	return out;
}

export default function TableHead({ langs, sort, onSort, allSelected, onSelectAll, onSelectNone }: Props) {
	const columns = React.useMemo(() => buildColumns(langs), [langs]);
	function handleSort(col: ColDef) {
		if (!col.sortable) return;
		onSort(col.key as SortState["column"]);
	}

	function getSortIcon(col: ColDef) {
		if (!col.sortable) return null;
		if (sort.column !== col.key) return <SortIcon>↕</SortIcon>;
		return <SortIcon>{sort.dir === "asc" ? "↑" : "↓"}</SortIcon>;
	}

	return (
		<StyledTHead>
			<tr>
				{columns.map((col) => (
					<StyledTh
						key={col.key}
						sortable={col.sortable}
						width={col.width}
						onClick={() => handleSort(col)}>
						{col.key === "_checkbox" ? (
							<input
								type="checkbox"
								checked={allSelected}
								onChange={allSelected ? onSelectNone : onSelectAll}
								onClick={(e) => e.stopPropagation()}
							/>
						) : (
							<>
								{col.label}
								{getSortIcon(col)}
							</>
						)}
					</StyledTh>
				))}
			</tr>
		</StyledTHead>
	);
}
